import * as React from 'react';
import { useSelectAsync } from './hooks/useSelectAsync';
import { SingleSelect } from './SingleSelect';
import type { IAsyncSelectProps, IOption, SelectOptionValue } from './types';

const keepAsyncOption = <TValue extends SelectOptionValue, TMetadata>(
	_option: IOption<TValue, TMetadata>,
	_query: string
): boolean => true;

export const AsyncSelect = <
	TValue extends SelectOptionValue = SelectOptionValue,
	TMetadata = unknown
>({
	defaultOpen = false,
	defaultSearchValue = '',
	errorLabel,
	filterOption = keepAsyncOption,
	hasNextPage,
	isError = false,
	isOpen: controlledIsOpen,
	isLoading = false,
	lastOptionRef,
	loadOnOpen = false,
	loadOptions,
	loadingMore = false,
	onChange,
	onOpenChange,
	onSearchChange,
	pagination,
	searchDebounceMs,
	searchValue,
	value,
	...props
}: IAsyncSelectProps<TValue, TMetadata>): React.ReactElement => {
	const [internalIsOpen, setInternalIsOpen] = React.useState(defaultOpen);
	const isOpen = controlledIsOpen ?? internalIsOpen;
	const resolvedSearchDebounceMs = searchDebounceMs ?? 0;
	const hasControlledSearchDebounce = searchValue !== undefined && resolvedSearchDebounceMs > 0;
	const searchLoadTimeoutRef = React.useRef<number>();
	const {
		handleLastOptionRef,
		handleOpenChange: handleAsyncOpenChange,
		handleSearchChange: handleAsyncSearchChange,
		isLoadingNextPage,
		loadStatus,
	} = useSelectAsync({
		hasNextPage,
		isError,
		isOpen,
		isLoading,
		lastOptionRef,
		loadOnOpen,
		loadOptions,
		onSearchChange: hasControlledSearchDebounce ? undefined : onSearchChange,
		pagination,
		query: searchValue ?? defaultSearchValue,
	});
	const isLoadingMore = loadingMore || isLoadingNextPage;

	React.useEffect(
		() => () => {
			window.clearTimeout(searchLoadTimeoutRef.current);
		},
		[]
	);

	const handleSearchChange = React.useCallback(
		(nextSearchValue: string) => {
			if (!hasControlledSearchDebounce) {
				handleAsyncSearchChange(nextSearchValue);
				return;
			}

			onSearchChange?.(nextSearchValue);
			window.clearTimeout(searchLoadTimeoutRef.current);
			searchLoadTimeoutRef.current = window.setTimeout(
				() => handleAsyncSearchChange(nextSearchValue),
				resolvedSearchDebounceMs
			);
		},
		[
			handleAsyncSearchChange,
			hasControlledSearchDebounce,
			onSearchChange,
			resolvedSearchDebounceMs,
		]
	);

	const handleOpenChange = React.useCallback(
		(nextIsOpen: boolean) => {
			if (controlledIsOpen === undefined) {
				setInternalIsOpen(nextIsOpen);
			}
			handleAsyncOpenChange(nextIsOpen);
			onOpenChange?.(nextIsOpen);
		},
		[controlledIsOpen, handleAsyncOpenChange, onOpenChange]
	);

	return (
		<SingleSelect
			{...props}
			defaultOpen={defaultOpen}
			defaultSearchValue={defaultSearchValue}
			error={loadStatus === 'error' ? errorLabel : undefined}
			filterOption={filterOption}
			isOpen={isOpen}
			lastOptionRef={handleLastOptionRef}
			loading={loadStatus === 'loading' && !isLoadingMore}
			loadingMore={isLoadingMore}
			onChange={onChange}
			onOpenChange={handleOpenChange}
			onSearchChange={handleSearchChange}
			searchDebounceMs={searchDebounceMs}
			searchable
			searchValue={searchValue}
			value={value}
		/>
	);
};
